import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, LifeBuoy, ShieldCheck, ShieldAlert, Building2, Clock,
  FileCheck, XCircle, RotateCcw, Fingerprint, Paperclip, User,
} from "lucide-react";
import { motion } from "framer-motion";
import { api, apiErrorText } from "@/lib/api";
import {
  VLKCard, VLKCardHeader, VLKCardTitle, VLKCardContent, VLKCardDescription,
  VLKButton, VLKStatusBadge, VLKRiskMeter, VLKAlertDialog, VLKLabel, VLKSelect,
  VLKTextarea, VLKInput, VLKUserAvatar, VLKEmptyState, VLKSkeleton, toast,
} from "@/components/vlk";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { IDS } from "@/constants/testIds";

const STATE_TONE = {
  SUBMITTED:            "pending",
  AWAITING_ATTESTATION: "pending",
  AWAITING_APPROVAL:    "pending",
  APPROVED:             "verified",
  REACTIVATED:          "active",
  REJECTED:             "rejected",
};

/* Progress dots */
const PIPELINE = [
  { key: "SUBMITTED",             label: "Submitted"        },
  { key: "AWAITING_ATTESTATION",  label: "Attestation"      },
  { key: "AWAITING_APPROVAL",     label: "Compliance"       },
  { key: "APPROVED",              label: "Approved"         },
  { key: "REACTIVATED",           label: "Reactivated"      },
];

function Progress({ state }) {
  const idx = PIPELINE.findIndex(p => p.key === state);
  const rejected = state === "REJECTED";
  return (
    <div className="flex items-center gap-2 w-full">
      {PIPELINE.map((p, i) => {
        const done = !rejected && i <= idx;
        const current = !rejected && i === idx;
        return (
          <React.Fragment key={p.key}>
            <div className="flex flex-col items-center gap-1 min-w-0">
              <div className={cn(
                "h-2.5 w-2.5 rounded-full border transition-colors",
                done ? "bg-foreground border-foreground" : "bg-transparent border-border",
                rejected && "border-border/40 bg-transparent",
                current && "ring-2 ring-foreground/20 ring-offset-2 ring-offset-background",
              )} />
              <div className={cn(
                "font-mono text-[9px] uppercase tracking-widest whitespace-nowrap",
                done ? "text-foreground" : "text-muted-foreground",
                rejected && "text-muted-foreground/50 line-through decoration-1",
              )}>{p.label}</div>
            </div>
            {i < PIPELINE.length - 1 && (
              <div className={cn(
                "h-px flex-1 mb-4",
                done && i < idx ? "bg-foreground/70" : "bg-border",
                rejected && "bg-border/40",
              )} />
            )}
          </React.Fragment>
        );
      })}
      {rejected && (
        <div className="ml-3 flex items-center gap-2 border border-destructive/40 rounded px-2 py-1 text-destructive">
          <XCircle className="h-3.5 w-3.5" />
          <span className="text-[10px] uppercase tracking-widest font-mono">Rejected</span>
        </div>
      )}
    </div>
  );
}

function Panel({ title, children, description }) {
  return (
    <VLKCard>
      <VLKCardHeader>
        <VLKCardTitle>{title}</VLKCardTitle>
        {description && <VLKCardDescription>{description}</VLKCardDescription>}
      </VLKCardHeader>
      <VLKCardContent>{children}</VLKCardContent>
    </VLKCard>
  );
}

function Meta({ label, children, mono = false }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5 border-b border-border/60 last:border-0">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground w-40 shrink-0">{label}</div>
      <div className={cn("text-sm min-w-0", mono && "font-mono text-xs")}>{children || <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}


export default function RecoveryDetail() {
  const { request_id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user: me } = useAuth();

  const [action, setAction] = useState(null); // "attest"|"approve"|"reject"|"reactivate"|"evidence"|null
  const [note, setNote] = useState("");
  const [inst, setInst] = useState("");
  const [evidence, setEvidence] = useState({ kind: "DOCUMENT", description: "", reference: "" });
  const [busy, setBusy] = useState(false);

  const { data: req, isLoading } = useQuery({
    queryKey: ["recovery", request_id],
    queryFn: () => api.get(`/recovery/requests/${request_id}`).then(r => r.data),
    enabled: !!request_id,
  });

  const { data: institutions } = useQuery({
    queryKey: ["institutions-select"],
    queryFn: () => api.get("/institutions").then(r => r.data),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["recovery", request_id] });
    qc.invalidateQueries({ queryKey: ["recovery-requests"] });
  };

  const call = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      setAction(null); setNote(""); setInst(""); setEvidence({ kind: "DOCUMENT", description: "", reference: "" });
      refresh();
    } catch (e) { toast.error("Action failed", { description: apiErrorText(e) }); }
    finally { setBusy(false); }
  };

  const doAttest = () => call(
    () => api.post(`/recovery/requests/${request_id}/attest`, { institution_vlk_id: inst, attestation_note: note }),
    "Attestation recorded"
  );
  const doDecide = (kind) => call(
    () => api.post(`/recovery/requests/${request_id}/decide`, { decision: kind, note }),
    kind === "APPROVE" ? "Approved" : "Rejected"
  );
  const doReactivate = () => call(
    () => api.post(`/recovery/requests/${request_id}/reactivate`),
    "Identity reactivated"
  );
  const doEvidence = () => call(
    () => api.post(`/recovery/requests/${request_id}/evidence`, evidence),
    "Evidence added"
  );

  if (isLoading || !req) {
    return <div className="space-y-4"><VLKSkeleton className="h-24" /><VLKSkeleton className="h-96" /></div>;
  }

  const role = me?.role;
  const canAttest      = ["system_admin", "institution_admin"].includes(role);
  const canDecide      = ["system_admin", "compliance_officer"].includes(role);
  const canReactivate  = ["system_admin", "security_officer"].includes(role);
  const canAddEvidence = ["SUBMITTED", "AWAITING_ATTESTATION", "AWAITING_APPROVAL"].includes(req.state);

  return (
    <div>
      <Link to="/recovery" data-testid={IDS.recoveryBack} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Recovery queue
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-card border border-border rounded p-6 mb-6"
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="h-14 w-14 rounded-sm border border-border flex items-center justify-center text-muted-foreground shrink-0">
              <LifeBuoy className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-2xl tracking-tight truncate">{req.identity_name}</h1>
                <VLKStatusBadge status={STATE_TONE[req.state] || "default"}>{req.state.replace(/_/g, " ")}</VLKStatusBadge>
              </div>
              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                <Link to={`/identities/${req.vlk_id}`} className="font-mono text-xs hover:text-foreground">{req.vlk_id}</Link>
                <span>·</span><span className="font-mono text-xs">{req.request_id}</span>
                <span>·</span><VLKRiskMeter level={req.risk_level} />
              </div>
              <div className="mt-3 text-sm max-w-3xl">{req.reason}</div>
            </div>
          </div>

          {/* Actions column, role-gated */}
          <div className="flex flex-col gap-2 shrink-0 min-w-[200px]">
            {req.state === "AWAITING_ATTESTATION" && canAttest && (
              <VLKButton variant="primary" onClick={() => { setAction("attest"); setNote(""); setInst(institutions?.items?.[0]?.vlk_inst_id || ""); }} data-testid={IDS.recoveryAttestBtn}>
                <FileCheck className="h-4 w-4" />Attest as institution
              </VLKButton>
            )}
            {req.state === "AWAITING_APPROVAL" && canDecide && (
              <>
                <VLKButton variant="primary" onClick={() => { setAction("approve"); setNote(""); }} data-testid={IDS.recoveryApproveBtn}>
                  <ShieldCheck className="h-4 w-4" />Approve
                </VLKButton>
                <VLKButton variant="outline" onClick={() => { setAction("reject"); setNote(""); }} data-testid={IDS.recoveryRejectBtn}>
                  <XCircle className="h-4 w-4" />Reject
                </VLKButton>
              </>
            )}
            {req.state === "APPROVED" && canReactivate && (
              <VLKButton variant="primary" onClick={() => setAction("reactivate")} data-testid={IDS.recoveryReactivateBtn}>
                <RotateCcw className="h-4 w-4" />Reactivate identity
              </VLKButton>
            )}
            {canAddEvidence && (
              <VLKButton variant="outline" onClick={() => setAction("evidence")} data-testid={IDS.recoveryEvidenceBtn}>
                <Paperclip className="h-4 w-4" />Add evidence
              </VLKButton>
            )}
          </div>
        </div>

        <div className="mt-6">
          <Progress state={req.state} />
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Request meta" description="Contact + provenance for this recovery.">
          <Meta label="Request ID" mono>{req.request_id}</Meta>
          <Meta label="Identity" mono>{req.vlk_id} · {req.identity_name}</Meta>
          <Meta label="Contact email" mono>{req.contact_email}</Meta>
          <Meta label="Contact phone" mono>{req.contact_phone}</Meta>
          <Meta label="Submitted by" mono>{req.submitted_by_email}</Meta>
          <Meta label="Submitted at" mono>{req.submitted_at && new Date(req.submitted_at).toLocaleString()}</Meta>
          <Meta label="Risk"><VLKRiskMeter level={req.risk_level} /></Meta>
        </Panel>

        <Panel title="Attestation" description="Institution officer confirms citizen identity.">
          {req.attestation ? (
            <div className="space-y-2">
              <Meta label="Institution"><Building2 className="h-3.5 w-3.5 inline mr-1.5" />{req.attestation.institution_name}</Meta>
              <Meta label="Attested by" mono>{req.attestation.attested_by_email}</Meta>
              <Meta label="Role" mono>{req.attestation.attested_by_role}</Meta>
              <Meta label="Attested at" mono>{new Date(req.attestation.attested_at).toLocaleString()}</Meta>
              <Meta label="Note">{req.attestation.note}</Meta>
            </div>
          ) : <VLKEmptyState icon={FileCheck} title="No attestation yet" description="Awaiting institution officer." />}
        </Panel>

        <Panel title="Compliance decision" description="Multi-party approval — irreversible when rejected.">
          {req.decision || req.rejection ? (
            <div className="space-y-2">
              {(req.decision || req.rejection) && (
                <>
                  <Meta label="Decision">
                    <VLKStatusBadge status={req.decision ? "verified" : "rejected"}>
                      {req.decision ? "APPROVED" : "REJECTED"}
                    </VLKStatusBadge>
                  </Meta>
                  <Meta label="By" mono>{(req.decision || req.rejection).decided_by_email}</Meta>
                  <Meta label="At" mono>{new Date((req.decision || req.rejection).decided_at).toLocaleString()}</Meta>
                  <Meta label="Note">{(req.decision || req.rejection).note}</Meta>
                </>
              )}
            </div>
          ) : <VLKEmptyState icon={ShieldCheck} title="Awaiting compliance" description="Decision not yet made." />}
        </Panel>

        <Panel title="Reactivation" description="Final security step — restores identity to ACTIVE.">
          {req.reactivation ? (
            <div className="space-y-2">
              <Meta label="Reactivated by" mono>{req.reactivation.reactivated_by_email}</Meta>
              <Meta label="Reactivated at" mono>{new Date(req.reactivation.reactivated_at).toLocaleString()}</Meta>
              <div className="mt-3 flex items-center gap-2 border border-success/30 bg-success/5 rounded-sm px-3 py-2">
                <ShieldCheck className="h-4 w-4 text-success" />
                <span className="text-sm text-success">Identity is active.</span>
              </div>
            </div>
          ) : <VLKEmptyState icon={RotateCcw} title="Not reactivated" description="Awaiting security officer once approved." />}
        </Panel>
      </div>

      {/* Evidence list */}
      <div className="mt-4">
        <Panel title={`Evidence (${req.evidence?.length ?? 0})`} description="Documents and attestations supporting recovery. Immutable once submitted.">
          {(req.evidence || []).length === 0 ? (
            <VLKEmptyState icon={Paperclip} title="No evidence yet" description="Attach the first item using the button above." />
          ) : (
            <div className="space-y-2">
              {req.evidence.map(e => (
                <div key={e.evidence_id} className="border border-border rounded p-3 bg-background flex items-start gap-3">
                  <div className="h-8 w-8 rounded-sm border border-border flex items-center justify-center shrink-0"><Paperclip className="h-4 w-4 text-muted-foreground" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <VLKStatusBadge status="verified">{e.kind}</VLKStatusBadge>
                      <span className="text-sm font-medium">{e.description}</span>
                    </div>
                    {e.reference && <div className="font-mono text-[10px] text-muted-foreground mt-1 truncate">{e.reference}</div>}
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono mt-1">
                      {e.uploaded_by_email} · {new Date(e.uploaded_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Dialogs */}
      <VLKAlertDialog
        open={action === "attest"}
        onOpenChange={() => setAction(null)}
        title="Attest recovery request"
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => setAction(null)}>Cancel</VLKButton>
            <VLKButton variant="primary" onClick={doAttest} loading={busy} disabled={!inst || note.length < 3} data-testid={IDS.recoveryAttestSubmit}>Attest</VLKButton>
          </>
        }
      >
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">Confirms the citizen's identity is on-file at your institution. Moves to <span className="font-mono">AWAITING_APPROVAL</span>.</div>
          <div className="space-y-1.5">
            <VLKLabel>Institution</VLKLabel>
            <VLKSelect value={inst} onChange={(e) => setInst(e.target.value)} data-testid={IDS.recoveryInstSelect}>
              {(institutions?.items || []).map(i => <option key={i.vlk_inst_id} value={i.vlk_inst_id}>{i.name}</option>)}
            </VLKSelect>
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Attestation note</VLKLabel>
            <VLKTextarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Records match. Recommend approval…" data-testid={IDS.recoveryAttestNote} />
          </div>
        </div>
      </VLKAlertDialog>

      <VLKAlertDialog
        open={action === "approve" || action === "reject"}
        onOpenChange={() => setAction(null)}
        title={action === "approve" ? "Approve recovery" : "Reject recovery"}
        tone={action === "reject" ? "destructive" : "default"}
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => setAction(null)}>Cancel</VLKButton>
            <VLKButton
              variant={action === "reject" ? "destructive" : "primary"}
              onClick={() => doDecide(action === "approve" ? "APPROVE" : "REJECT")}
              loading={busy}
              data-testid={IDS.recoveryDecisionSubmit}
            >Confirm</VLKButton>
          </>
        }
      >
        <div className="space-y-3">
          <div className="text-sm">
            {action === "approve"
              ? "Approval moves the request to APPROVED, awaiting security officer reactivation."
              : "Rejection is final. Identity remains in its current state."}
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Compliance note (optional)</VLKLabel>
            <VLKTextarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid={IDS.recoveryDecisionNote} />
          </div>
        </div>
      </VLKAlertDialog>

      <VLKAlertDialog
        open={action === "reactivate"}
        onOpenChange={() => setAction(null)}
        title="Reactivate identity"
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => setAction(null)}>Cancel</VLKButton>
            <VLKButton variant="primary" onClick={doReactivate} loading={busy} data-testid={IDS.recoveryReactivateSubmit}>Reactivate</VLKButton>
          </>
        }
      >
        <div className="text-sm">
          This restores <span className="font-mono">{req.vlk_id}</span> to <span className="text-success font-mono">ACTIVE</span> and closes the recovery workflow. This action is auditable and cannot be reversed by rolling back the workflow.
        </div>
      </VLKAlertDialog>

      <VLKAlertDialog
        open={action === "evidence"}
        onOpenChange={() => setAction(null)}
        title="Add evidence"
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => setAction(null)}>Cancel</VLKButton>
            <VLKButton variant="primary" onClick={doEvidence} loading={busy} disabled={evidence.description.length < 3} data-testid={IDS.recoveryEvidenceSubmit}>Add</VLKButton>
          </>
        }
      >
        <div className="space-y-3">
          <div className="space-y-1.5">
            <VLKLabel>Kind</VLKLabel>
            <VLKSelect value={evidence.kind} onChange={(e) => setEvidence({ ...evidence, kind: e.target.value })} data-testid={IDS.recoveryEvidenceKind}>
              <option value="DOCUMENT">Document</option>
              <option value="PHOTO_ID">Photo ID</option>
              <option value="AFFIDAVIT">Affidavit</option>
              <option value="INSTITUTION_LETTER">Institution letter</option>
              <option value="OTHER">Other</option>
            </VLKSelect>
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Description</VLKLabel>
            <VLKInput value={evidence.description} onChange={(e) => setEvidence({ ...evidence, description: e.target.value })} placeholder="Notarized affidavit + photo ID" data-testid={IDS.recoveryEvidenceDesc} />
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Reference (URL / hash / doc id)</VLKLabel>
            <VLKInput value={evidence.reference} onChange={(e) => setEvidence({ ...evidence, reference: e.target.value })} placeholder="vault://…" className="font-mono" />
          </div>
        </div>
      </VLKAlertDialog>
    </div>
  );
}
